import { Injectable } from '@angular/core';

import { Project } from '../models/project';

export interface TimesheetCellKeyActions {
  openNotes(project: Project, day: string): void;
  startTimer(project: Project, day: string): void;
  togglePin(project: Project): void;
}

@Injectable()
export class TimesheetKeyboardNavigationService {
  private lastHoursInputIndex: number | null = null;

  handleNavigation(e: KeyboardEvent): void {
    const inputElement = e.target as HTMLInputElement;
    const currentTd = inputElement.closest('td');
    if (!currentTd) return;

    const currentRow = currentTd.parentElement as HTMLTableRowElement;
    const table = currentRow.closest('table');
    if (!table) return;

    const allRows = Array.from(table.querySelectorAll('tr'));
    const rowIndex = allRows.indexOf(currentRow);
    const cellIndex = Array.from(currentRow.children).indexOf(currentTd);
    if (currentTd.querySelector('input')) {
      this.lastHoursInputIndex = cellIndex;
    }

    const allSelected =
      inputElement.selectionStart === 0 &&
      inputElement.selectionEnd === inputElement.value.length;

    const leftInput =
      cellIndex > 0 && (allSelected || inputElement.selectionStart === 0)
        ? this.findInputInCell(currentRow.children[cellIndex - 1])
        : null;
    const rightInput =
      cellIndex < currentRow.children.length - 1 &&
      (allSelected || inputElement.selectionStart === inputElement.value.length)
        ? this.findInputInCell(currentRow.children[cellIndex + 1])
        : null;
    const upInput = this.findVerticalInput(allRows, rowIndex, cellIndex, -1);
    const downInput = this.findVerticalInput(allRows, rowIndex, cellIndex, 1);

    switch (e.key) {
      case 'ArrowLeft':
        this.focusInput(leftInput, e);
        break;
      case 'ArrowRight':
        this.focusInput(rightInput, e);
        break;
      case 'ArrowUp':
        this.focusInput(upInput, e);
        break;
      case 'F2':
        if (!allSelected) break;
        this.focusDownOrEdit(inputElement, downInput, e);
        break;
      case 'Enter':
        if (inputElement.classList.contains('expando-collapso')) break;
        this.focusDownOrEdit(inputElement, downInput, e);
        break;
      case 'ArrowDown':
        this.focusInput(downInput, e);
        break;
    }
  }

  handleHoursKey(
    e: KeyboardEvent,
    project: Project,
    day: string,
    allowTwoDigitEntry: boolean,
    actions: TimesheetCellKeyActions,
  ): void {
    switch (e.key.toLowerCase()) {
      case 'n':
        actions.openNotes(project, day);
        break;
      case 't':
        actions.startTimer(project, day);
        break;
      case 'p':
        actions.togglePin(project);
        break;
    }

    if (!this.isAllowedHoursInput(e, allowTwoDigitEntry)) {
      e.preventDefault();
    }
  }

  private findVerticalInput(
    allRows: HTMLTableRowElement[],
    rowIndex: number,
    cellIndex: number,
    dir: -1 | 1,
  ): HTMLInputElement | null {
    for (
      let i = rowIndex + dir;
      dir == -1 ? i >= 0 : i < allRows.length;
      i += dir
    ) {
      const input = this.findInputInCell(allRows[i].children[cellIndex]);
      if (input) return input;
    }
    return null;
  }

  private findInputInCell(cell: Element): HTMLInputElement | null {
    return cell && this.isVisible(cell)
      ? ((cell.querySelector('input') as HTMLInputElement) ??
          cell.closest('tr')?.querySelector('.expando-collapso') ??
          cell
            .closest('tr')
            ?.querySelectorAll(`td`)
            [this.lastHoursInputIndex ?? 0]?.querySelector('input') ??
          cell.closest('tr')?.querySelector('input'))
      : null;
  }

  private focusDownOrEdit(
    inputElement: HTMLInputElement,
    downInput: HTMLInputElement | null,
    e: KeyboardEvent,
  ): void {
    const allSelected =
      inputElement.selectionStart === 0 &&
      inputElement.selectionEnd === inputElement.value.length;

    if (!allSelected) {
      this.focusInput(downInput, e);
      return;
    }

    inputElement.selectionStart = inputElement.selectionEnd =
      inputElement.value.length;
  }

  private focusInput(input: HTMLInputElement | null, e: KeyboardEvent): void {
    if (!input) return;

    input.focus();
    input.select?.();
    e.preventDefault();
  }

  private isVisible(cell: Element): boolean {
    const rect = cell.getBoundingClientRect();
    const isVisible = cell.checkVisibility({
      checkOpacity: true,
      checkVisibilityCSS: true,
      contentVisibilityAuto: true,
      opacityProperty: true,
      visibilityProperty: true,
    });
    return rect.height > 0 && rect.width > 0 && isVisible;
  }

  private isAllowedHoursInput(
    e: KeyboardEvent,
    allowTwoDigitEntry: boolean,
  ): boolean {
    const target = e.target as HTMLInputElement;
    const newChar = e.key;
    const cursorPos = target.selectionStart ?? 0;
    const selEnd = target.selectionEnd ?? cursorPos;
    const inputText = target.value;
    const proposedNewText = `${inputText.slice(0, cursorPos)}${newChar}${inputText.slice(selEnd, inputText.length)}`;
    const allowedFormat = allowTwoDigitEntry
      ? /^(?:(1[0-9])|(2[0-4])|[0-9]?)(\.[0-9]{0,2})?$/
      : /^(?:(1[0-9])|(2[0-4])|[0-9]?)(\.[0-9]{0,1})?$/;

    return allowedFormat.test(proposedNewText);
  }
}
